// packages/web/src/stores/exerciseLibraryStore.ts
import { create } from 'zustand';
import { teacherExerciseApi, ExerciseTemplate } from '../services/teacherExerciseApi';

export interface ExerciseLibraryItem extends ExerciseTemplate {
  selected: boolean;
  isEditingRequirement: boolean;
}

export interface TimelineGroup {
  date: string;
  label: string;
  exercises: ExerciseLibraryItem[];
}

interface ExerciseLibraryState {
  exercises: ExerciseLibraryItem[];
  loading: boolean;
  error: string | null;
  
  // 筛选条件
  categoryFilter: string;
  assignmentStatusFilter: 'all' | 'assigned' | 'unassigned';
  dateRangeFilter: { start?: string; end?: string };
  
  // 模态窗口
  showDetailModal: boolean;
  showAddToHomeworkModal: boolean;
  selectedExerciseForDetail: ExerciseLibraryItem | null;
  
  // Actions
  fetchExercises: () => Promise<void>;
  setCategoryFilter: (category: string) => void;
  setAssignmentStatusFilter: (status: 'all' | 'assigned' | 'unassigned') => void;
  setDateRangeFilter: (range: { start?: string; end?: string }) => void;
  toggleExerciseSelection: (id: string) => void;
  selectAllExercises: () => void;
  deselectAllExercises: () => void;
  updateExerciseRequirement: (id: string, requirement: string) => Promise<void>;
  deleteExercises: (ids: string[]) => Promise<void>;
  setEditingRequirement: (id: string, isEditing: boolean) => void;
  setShowDetailModal: (show: boolean) => void;
  setShowAddToHomeworkModal: (show: boolean) => void;
  setSelectedExerciseForDetail: (exercise: ExerciseLibraryItem | null) => void;
  groupByTimeline: () => TimelineGroup[];
}

export const useExerciseLibraryStore = create<ExerciseLibraryState>((set, get) => ({
  exercises: [],
  loading: false,
  error: null,
  categoryFilter: 'all',
  assignmentStatusFilter: 'all',
  dateRangeFilter: {},
  showDetailModal: false,
  showAddToHomeworkModal: false,
  selectedExerciseForDetail: null,
  
  fetchExercises: async () => {
    set({ loading: true, error: null });
    try {
      const { categoryFilter, assignmentStatusFilter, dateRangeFilter } = get();
      const filters = {
        category: categoryFilter !== 'all' ? categoryFilter : undefined,
        assignmentStatus: assignmentStatusFilter,
        startDate: dateRangeFilter.start,
        endDate: dateRangeFilter.end,
      };
      
      const response = await teacherExerciseApi.getTemplates(filters);
      const exercises = response.data.exercises.map(ex => ({
        ...ex,
        selected: false,
        isEditingRequirement: false,
      }));
      
      set({ exercises, loading: false });
    } catch (error: any) {
      set({ 
        error: error.response?.data?.error || 'Failed to fetch exercises',
        loading: false 
      });
    }
  },
  
  setCategoryFilter: (category) => {
    set({ categoryFilter: category });
    get().fetchExercises();
  },
  
  setAssignmentStatusFilter: (status) => {
    set({ assignmentStatusFilter: status });
    get().fetchExercises();
  },
  
  setDateRangeFilter: (range) => {
    set({ dateRangeFilter: range });
    get().fetchExercises();
  },
  
  toggleExerciseSelection: (id) => set((state) => ({
    exercises: state.exercises.map(ex =>
      ex.id === id ? { ...ex, selected: !ex.selected } : ex
    ),
  })),
  
  selectAllExercises: () => set((state) => ({
    exercises: state.exercises.map(ex => ({ ...ex, selected: true })),
  })),
  
  deselectAllExercises: () => set((state) => ({
    exercises: state.exercises.map(ex => ({ ...ex, selected: false })),
  })),
  
  updateExerciseRequirement: async (id, requirement) => {
    try {
      await teacherExerciseApi.updateRequirement(id, requirement);
      set((state) => ({
        exercises: state.exercises.map(ex =>
          ex.id === id ? { ...ex, teacher_requirement: requirement } : ex
        ),
      }));
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to update requirement' });
      throw error;
    }
  },
  
  deleteExercises: async (ids) => {
    try {
      const response = await teacherExerciseApi.batchDelete(ids);
      
      // 从列表中移除已删除的习题
      set((state) => ({
        exercises: state.exercises.filter(ex => !ids.includes(ex.id) || response.data.skippedIds.includes(ex.id)),
      }));
      
      // 如果有跳过的习题，显示提示
      if (response.data.skippedCount > 0) {
        set({ 
          error: `${response.data.deletedCount} exercises deleted, ${response.data.skippedCount} skipped (already assigned)` 
        });
      }
    } catch (error: any) {
      set({ error: error.response?.data?.error || 'Failed to delete exercises' });
      throw error;
    }
  },
  
  setEditingRequirement: (id, isEditing) => set((state) => ({
    exercises: state.exercises.map(ex =>
      ex.id === id ? { ...ex, isEditingRequirement: isEditing } : ex
    ),
  })),
  
  setShowDetailModal: (show) => set({ showDetailModal: show }),
  
  setShowAddToHomeworkModal: (show) => set({ showAddToHomeworkModal: show }),
  
  setSelectedExerciseForDetail: (exercise) => set({ 
    selectedExerciseForDetail: exercise,
    showDetailModal: !!exercise,
  }),
  
  groupByTimeline: () => {
    const { exercises } = get();
    const groups: { [key: string]: ExerciseLibraryItem[] } = {};
    
    exercises.forEach(ex => {
      const date = new Date(ex.created_at);
      const dateKey = date.toISOString().split('T')[0];
      
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(ex);
    });
    
    const sortedKeys = Object.keys(groups).sort((a, b) => 
      new Date(b).getTime() - new Date(a).getTime()
    );
    
    return sortedKeys.map(dateKey => {
      const date = new Date(dateKey);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      
      let label: string;
      if (dateKey === today.toISOString().split('T')[0]) {
        label = '今天';
      } else if (dateKey === yesterday.toISOString().split('T')[0]) {
        label = '昨天';
      } else {
        label = date.toLocaleDateString('zh-CN', { 
          year: 'numeric', 
          month: 'long', 
          day: 'numeric' 
        });
      }
      
      return {
        date: dateKey,
        label,
        exercises: groups[dateKey],
      };
    });
  },
}));
